import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import migrationService from '../../services/migrationService';

const PendingMigrationLinksModal = ({ pendingLinks, onAllResolved }) => {
  const [links, setLinks] = useState(pendingLinks);
  const [selections, setSelections] = useState({});
  const [resolving, setResolving] = useState({});
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const handleSelect = (linkId, caseId) => {
    setSelections(prev => ({ ...prev, [linkId]: caseId }));
    setErrors(prev => ({ ...prev, [linkId]: null }));
  };

  const handleResolve = async (linkId) => {
    const chosenCaseId = selections[linkId];
    if (!chosenCaseId) {
      setErrors(prev => ({ ...prev, [linkId]: 'Please select a case' }));
      return;
    }

    setResolving(prev => ({ ...prev, [linkId]: true }));
    try {
      await migrationService.resolvePendingLink(linkId, chosenCaseId);
      const updated = links.filter(l => l.id !== linkId);
      setLinks(updated);
      if (updated.length === 0) {
        onAllResolved();
      }
    } catch (err) {
      setErrors(prev => ({ ...prev, [linkId]: 'Failed to resolve. Please try again.' }));
    } finally {
      setResolving(prev => ({ ...prev, [linkId]: false }));
    }
  };

  const handleSkip = () => {
    onAllResolved();
  };

  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    const num = parseFloat(String(value).replace(/[$,]/g, ''));
    return isNaN(num) ? value : `$${num.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50" />

      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Imported Invoices Need Your Attention
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              These invoices were imported from Practice Panther but the contact has multiple cases.
              Please select which case each invoice belongs to.
            </p>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {links.map((link) => {
              const summary = link.entity_summary || {};
              const candidates = link.candidate_cases || [];

              return (
                <div key={link.id} className="mb-6 border border-gray-200 rounded-lg p-4">
                  {/* Invoice info */}
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        Invoice #{summary.invoice_number}
                      </span>
                      <span className="mx-2 text-gray-300">|</span>
                      <span className="text-sm text-gray-600">
                        {summary.contact_name}
                      </span>
                      <span className="mx-2 text-gray-300">|</span>
                      <span className="text-sm text-gray-600">
                        {summary.date}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-semibold text-gray-900">
                        {formatCurrency(summary.total)}
                      </span>
                      <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        summary.status?.toLowerCase().includes('paid')
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {summary.status}
                      </span>
                    </div>
                  </div>

                  {/* Case options */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Which case does this invoice belong to?
                    </p>
                    {candidates.map((c) => (
                      <label
                        key={c.id}
                        className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                          selections[link.id] === c.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name={`link-${link.id}`}
                          value={c.id}
                          checked={selections[link.id] === c.id}
                          onChange={() => handleSelect(link.id, c.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="ml-3 flex-1">
                          <span className="text-sm font-medium text-gray-900">
                            {c.title}
                          </span>
                          <span className="ml-2 text-xs text-gray-500">
                            ({c.case_number})
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/cases/${c.id}`);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          View case
                        </button>
                      </label>
                    ))}
                  </div>

                  {/* Error & resolve button */}
                  <div className="mt-3 flex items-center justify-between">
                    {errors[link.id] && (
                      <p className="text-sm text-red-600">{errors[link.id]}</p>
                    )}
                    <div className="ml-auto">
                      <button
                        onClick={() => handleResolve(link.id)}
                        disabled={!selections[link.id] || resolving[link.id]}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {resolving[link.id] ? 'Saving...' : 'Link to this case'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 flex justify-between items-center">
            <p className="text-sm text-gray-500">
              {links.length} invoice{links.length !== 1 ? 's' : ''} remaining
            </p>
            <button
              onClick={handleSkip}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Remind me later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingMigrationLinksModal;
